import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Search, Filter, Music, User, Calendar, Eye } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const LyricsGalleryPage = () => {
  const [lyrics, setLyrics] = useState([]);
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const itemsPerPage = 20;

  useEffect(() => {
    loadGenres();
  }, []);

  useEffect(() => {
    loadLyrics();
  }, [selectedGenre, searchTerm, page]);

  const loadGenres = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/gallery/genres`);
      setGenres(response.data.genres || []);
    } catch (error) {
      console.error('Erro ao carregar gêneros:', error);
    }
  };

  const loadLyrics = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (page - 1) * itemsPerPage,
        limit: itemsPerPage
      };
      
      if (selectedGenre) params.genre = selectedGenre;
      if (searchTerm) params.search = searchTerm;
      
      const response = await axios.get(`${BACKEND_URL}/api/gallery/lyrics`, { params });
      setLyrics(response.data.lyrics || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Erro ao carregar letras:', error);
      toast.error('Erro ao carregar letras');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadLyrics();
  };

  const truncateText = (text, maxLength = 200) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const totalPages = Math.ceil(total / itemsPerPage);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <SEOHead
        title="Galeria de Letras de Músicas"
        description="Explore nossa biblioteca pública de letras de músicas registradas. Encontre composições por gênero, autor e trechos de letra. Todas as obras protegidas com certificado digital."
        keywords="letras de músicas, composições, direitos autorais, galeria de letras, música brasileira"
        type="website"
      />
      
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block bg-purple-100 p-4 rounded-full mb-4">
            <FileText className="w-12 h-12 text-purple-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Galeria Pública de Letras
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore nossa biblioteca de letras registradas e protegidas.
            Todas as obras com certificado digital e prova de autoria.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {total} letras disponíveis
          </p>
        </div>

        {/* Search and Filter */}
        <div className="max-w-4xl mx-auto mb-8">
          <form onSubmit={handleSearch} className="flex gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar por título, autor ou trecho da letra..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            <button
              type="submit"
              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Buscar
            </button>
          </form>

          {/* Genre Filter */}
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Filtrar por gênero:</span>
            <button
              onClick={() => setSelectedGenre('')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedGenre === ''
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Todos
            </button>
            {genres.map((genre) => (
              <button
                key={genre}
                onClick={() => setSelectedGenre(genre)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedGenre === genre
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {genre}
              </button>
            ))}
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando letras...</p>
          </div>
        )}

        {/* Lyrics Grid */}
        {!loading && lyrics.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {lyrics.map((lyric) => (
              <Link
                key={lyric.id}
                to={`/lyric/${lyric.id}`}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all p-6 border border-gray-200 hover:border-purple-300"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {lyric.title}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                      <User className="w-4 h-4" />
                      <span>{lyric.author_name || 'Autor'}</span>
                    </div>
                  </div>
                  {lyric.genre && (
                    <span className="px-3 py-1 bg-purple-100 text-purple-600 rounded-full text-xs font-medium">
                      {lyric.genre}
                    </span>
                  )}
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 mb-3 min-h-[120px]">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {truncateText(lyric.lyrics, 150)}
                  </p>
                </div>
                
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>
                      {new Date(lyric.created_at).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  {lyric.views > 0 && (
                    <div className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      <span>{lyric.views} visualizações</span>
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && lyrics.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Nenhuma letra encontrada
            </h3>
            <p className="text-gray-600">
              Tente ajustar os filtros ou buscar por outros termos.
            </p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Anterior
            </button>
            
            <div className="flex gap-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`px-4 py-2 rounded-lg ${
                      page === pageNum
                        ? 'bg-purple-600 text-white'
                        : 'border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Próxima
            </button>
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 text-white">
          <h2 className="text-3xl font-bold mb-4">
            Registre Suas Composições
          </h2>
          <p className="text-lg mb-6 opacity-90">
            Proteja suas letras com certificado digital e compartilhe na galeria pública.
          </p>
          <Link
            to="/register"
            className="inline-block bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Começar Agora - Grátis
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LyricsGalleryPage;
